import { FC, useState } from "react";
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  InputAdornment,
} from "@mui/material";
import { Lock } from "@mui/icons-material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import Input from "../formControls/input";
import ContainedButton from "../button/containedButton";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { handleChangePassword } from "../../pages/login/services/login.action";
import { ResponseCode } from "../../common/constants";
import { showAlert } from "../../common/constants/alert";
import { COLORS } from "../../common/constants";
import useStyles from "./styles";

interface ForcePasswordChangeDialogProps {
  open: boolean;
  onClose: () => void;
  forceChange?: boolean;
}

const forcePasswordSchema = Yup.object().shape({
  oldPassword: Yup.string().required("Old password is required"),
  newPassword: Yup.string()
    .min(8, "New password must be at least 8 characters")
    .required("New password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("newPassword")], "Passwords must match")
    .required("Confirm password is required"),
});

const ForcePasswordChangeDialog: FC<ForcePasswordChangeDialogProps> = ({
  open,
  onClose,
  forceChange = false,
}) => {
  const styles = useStyles();
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useAppDispatch();

  const handleSubmit = async (values: {
    oldPassword: string;
    newPassword: string;
    confirmPassword: string;
  }) => {
    setIsLoading(true);
    try {
      const userString = localStorage.getItem("user");
      const user = userString ? JSON.parse(userString) : null;
      const userId = user?.userId;

      if (!userId) {
        showAlert(2, "User information is missing. Please log in again.");
        return;
      }

      await dispatch(
        handleChangePassword({
          userId,
          oldPassword: values.oldPassword,
          newPassword: values.newPassword,
          successFn: () => {
            localStorage.setItem("forcePasswordChange", JSON.stringify(false));
            onClose();
          },
        })
      ).unwrap();
    } catch {
      // errors already handled in thunk
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <DialogContent sx={styles.dialogContent}>
        <Formik
          initialValues={{
            oldPassword: "",
            newPassword: "",
            confirmPassword: "",
          }}
          validationSchema={forcePasswordSchema}
          onSubmit={handleSubmit}
        >
          {() => (
            <Box component={Form} sx={styles.form}>
              <Box>
                <Typography sx={styles.dialogTitle}>
                  Change your password
                </Typography>
                {forceChange && (
                  <Typography sx={styles.description}>
                    For security reasons, you need to update your password
                    before continuing.
                  </Typography>
                )}
              </Box>
              <Input
                name="oldPassword"
                label="Current password"
                type="password"
                fullWidth
                autoComplete="current-password"
                placeholder="Enter your current password"
                externalStyles={styles.textField}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock
                        sx={{
                          color: COLORS.primary,
                          fontSize: "16px",
                          opacity: 0.7,
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
              <Input
                name="newPassword"
                label="New password"
                type="password"
                fullWidth
                autoComplete="new-password"
                placeholder="Enter your new password"
                externalStyles={styles.textField}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock
                        sx={{
                          color: COLORS.primary,
                          fontSize: "16px",
                          opacity: 0.7,
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
              <Input
                name="confirmPassword"
                label="Confirm new password"
                type="password"
                fullWidth
                autoComplete="new-password"
                placeholder="Re-enter your new password"
                externalStyles={styles.textField}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock
                        sx={{
                          color: COLORS.primary,
                          fontSize: "16px",
                          opacity: 0.7,
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
              <DialogActions sx={styles.dialogActions}>
                <ContainedButton
                  title={isLoading ? "Saving..." : "Change Password"}
                  type="submit"
                  disabled={isLoading}
                  externalStyles={styles.button}
                />
              </DialogActions>
            </Box>
          )}
        </Formik>
      </DialogContent>
    </>
  );
};

export default ForcePasswordChangeDialog;
